import ModalViewModel from '../../infra/ModalViewModel'
import { makeObservable, override } from 'mobx'
import { OpportunityOffer } from './OpportunityOffer'
import { task } from 'mobx-task'
import { OpportunityOfferStore } from './OpportunityOfferStore'
import { OpportunitiesPort } from '../ports'
import ConsultationModalViewModel, {
  ConsultationDetails,
} from '../views/ConsultationModal/ConsultationModal'
import { Id, Router } from '../../types/definitions'
import { filter } from 'rxjs/operators'
import link from '../../infra/link'
import { AlertFactory, AlertState } from '../../alerts/Alert'

/**
 * View Model for the Offer Modal.
 */
export class OfferModalViewModel extends ModalViewModel {
  constructor(
    private readonly _opportunityOfferStore: OpportunityOfferStore,
    private readonly _opportunityOffer: OpportunityOffer,
    private readonly _consultationModalViewModel: ConsultationModalViewModel,
    private readonly _opportunitiesPort: OpportunitiesPort,
    private readonly _router: Router,
    private readonly _newAlert: AlertFactory,
  ) {
    super()
    makeObservable(this)

    // Deflate the modal when an opportunity or its offer is removed.
    this._opportunityOfferStore.offersRemoved$
      .pipe(
        this.whileShowing(),
        filter((o) => o.id === _opportunityOffer.id),
      )
      .subscribe(this.close)
  }

  /**
   * The ID of the opportunity.
   */
  get id() {
    return this._opportunityOffer.id
  }

  /**
   * The name of the workspace where the offer exists.
   */
  get workspaceName() {
    return this._opportunityOffer.workspace.name
  }

  /**
   * The display name for the job.
   */
  get displayName() {
    return this._opportunityOffer.displayName
  }

  /**
   * The first and last name of the job's client.
   */
  get championClientName() {
    return this._opportunityOffer.client.displayName
  }

  /**
   * The provider cut amount for the job.
   */
  get proCut() {
    return this._opportunityOffer.formattedProviderCutAmount
  }

  /**
   * The list of job specs.
   */
  get jobSpecs() {
    return this._opportunityOffer.specs
  }

  /**
   * Indicates what action will be taken upon action expiration.
   */
  get offerExpirationAction() {
    return this._opportunityOffer.expirationAction
  }

  /**
   * The time until offer expiration.
   */
  get timeUntilExpiration() {
    return this._opportunityOffer.timeUntilExpiration
  }

  /**
   * The date at which the offer expires, in milliseconds.
   */
  get expirationMillis() {
    return this._opportunityOffer.expirationMillis
  }

  /**
   * Closes the offer modal as well as the consultation modal if it's showing.
   */
  @override
  close() {
    this._consultationModalViewModel.close()
    super.close()
  }

  /**
   * Accepts the given opportunity.
   */
  acceptOpportunity = task.resolved(async () => {
    const id = Id.assert(this._opportunityOffer.id)
    const consultation = this._opportunityOffer.consultation

    // Just accept the offer when there is no associated consultation.
    if (!consultation) {
      await this._opportunityOfferStore
        .acceptOffer(this._opportunityOffer.id)
        .then(this.close)
        .then(this.afterOfferAccepted)

      return
    }

    // Just accept the offer when there is a consultation, but no selected times.
    if (
      !consultation.availabilityTimes ||
      consultation.availabilityTimes.length === 0
    ) {
      await this._opportunityOfferStore
        .acceptOffer(this._opportunityOffer.id)
        .then(this.close)
        .then(this.afterOfferAccepted)

      return
    }

    const details: ConsultationDetails = {
      requestedLength: consultation.requestedLength,
      availability: consultation.availabilityTimes,
      bookedConsultations: [],
      client: this._opportunityOffer.client.user,
    }

    // prettier-ignore
    const bookedTime = await this
      ._consultationModalViewModel
      .promptForTimeSlots(details, this._opportunityOffer.currentMember)

    if (bookedTime) {
      await this._opportunitiesPort
        .pickupConsultation(id, bookedTime)
        .then(this.close)
        .then(this.afterOfferAccepted)
    }
  })

  /**
   * Declines the given opportunity.
   */
  declineOpportunity = task.resolved(async () => {
    await this._opportunityOfferStore
      .declineOffer(this._opportunityOffer.id)
      .then(this.close)
  })

  /**
   * Actions to take after an offer has been accepted.
   */
  private afterOfferAccepted = () => {
    this._newAlert()
      .withState(AlertState.Success)
      .saying('You accepted an offer.', 'Click to start work on your job.')
      .autoDismiss()
      .onInteract(() => {
        this._router.push(link.job(Id.assert(this._opportunityOffer.id)))
      })
      .show()
  }
}
